import * as React from 'react'
import { useState, useRef, useEffect } from 'react'
import {
  TransformWrapper,
  TransformComponent,
  useControls,
} from 'react-zoom-pan-pinch'
import {
  setGACustomEvent,
  setGACustomPageView,
} from '../../../../services/analytics/google-analytics'

import bgVideo from '../../../../assets/img/trade-hall/space-sample-video.mp4'
import spaceLogo from '../../../../assets/img/trade-hall/space-logo-white.webp'
import taglineLogo from '../../../../assets/img/trade-hall/tagline.webp'
import triangleLogo from '../../../../assets/img/trade-hall/triangle-a.webp'
import space1Image from '../../../../assets/img/trade-hall/space-1.png'
import space2Image from '../../../../assets/img/trade-hall/space-2.png'
import space3Image from '../../../../assets/img/trade-hall/space-3.png'
import space4Image from '../../../../assets/img/trade-hall/space-4.png'
import floorPlanImage from '../../../../assets/img/trade-hall/floor-plan.webp'
import descriptionBg from '../../../../assets/img/trade-hall/description-bg.jpg'
import socialFacebook from '../../../../assets/img/trade-hall/social-facebook.webp'
import socialInstagram from '../../../../assets/img/trade-hall/social-instagram.webp'
import {
  MainContainer,
  HeroSection,
  BackgroundVideo,
  HeroContent,
  HeroLogoArea,
  SpaceLogo,
  TaglineLogo,
  ButtonContainer,
  PrimaryButton,
  SecondaryButton,
  AboutSection,
  CarouselContainer,
  CarouselTrack,
  CarouselSlide,
  CarouselImage,
  CarouselArrow,
  ContentSection,
  TriangleLogo,
  TitleTaglineContainer,
  MainTitle,
  HighlightedText,
  Description,
  BoldText,
  StatsContainer,
  StatsInfo,
  StatsNumber,
  StatsMeasurements,
  ViewFloorPlanButton,
  FeaturesContainer,
  FeatureItem,
  FeatureNumber,
  FeatureText,
  Divider,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  ControlsContainer,
  ControlButton,
  ViewStatsContainer,
  SpaceDescriptionContainer,
  DescriptionRow,
  DescriptionCard,
  DescriptionCardTitle,
  DescriptionCardText,
  FinalTitle,
  SocialFollowSection,
  SocialFollowHeading,
  SocialIconsRow,
  SocialIconLink,
  SocialIconImg,
} from './MainView.style'
import { Helmet } from 'react-helmet'

const TRADE_HALL_FEATURES = [
  { number: '4', lines: ['Main', 'Halls'] },
  { number: '6', lines: ['Function', 'Rooms'] },
  { number: '3', lines: ['Service', 'Elevators'] },
  { number: '1', lines: ['Car', 'Lift'] },
]

const TRADE_HALL_SOCIAL_LINKS = {
  facebook: 'https://www.facebook.com/SPACEatOneAyala',
  instagram: 'https://www.instagram.com/spaceatoneayala',
}

export default function MainView() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(2) // Start at index 2 to show first real image
  const [isTransitioning, setIsTransitioning] = useState(true)
  const videoRef = useRef(null)
  const heroSectionRef = useRef(null)

  const playVideoWhenReady = React.useCallback(() => {
    const video = videoRef.current
    if (!video) return
    video.muted = true
    const tryPlay = () => {
      requestAnimationFrame(() => {
        setTimeout(() => video.play().catch(() => {}), 50)
      })
    }
    if (video.readyState >= 2) {
      tryPlay()
    } else {
      video.addEventListener('loadedmetadata', tryPlay, { once: true })
      video.addEventListener('canplay', tryPlay, { once: true })
    }
  }, [])

  useEffect(() => {
    const wrapper = heroSectionRef.current
    const video = videoRef.current
    if (!wrapper || !video) return

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const el = videoRef.current
          if (!el) return
          if (entry.isIntersecting) {
            el.play().catch(() => {})
          } else {
            el.pause()
          }
        })
      },
      { rootMargin: '0px', threshold: 0.1 },
    )
    observer.observe(wrapper)
    return () => observer.unobserve(wrapper)
  }, [])

  const spaceImages = [
    { src: space1Image, alt: 'Space 1' },
    { src: space2Image, alt: 'Space 2' },
    { src: space3Image, alt: 'Space 3' },
    { src: space4Image, alt: 'Space 4' },
  ]

  // Create infinite array: [last2, last1, img1, img2, img3, img4, img1, img2]
  const infiniteImages = [
    ...spaceImages.slice(-2), // Last 2 images at the start
    ...spaceImages, // All original images
    ...spaceImages.slice(0, 2), // First 2 images at the end
  ]

  // Max slide index when showing 2 images: spaceImages.length (shows last pair: img3, img4)
  const maxSlide = spaceImages.length

  const nextSlide = () => {
    setIsTransitioning(true)
    setCurrentSlide(prev => {
      const next = prev + 1
      // If we're past the max, jump to start after transition
      if (next > maxSlide) {
        setTimeout(() => {
          setIsTransitioning(false)
          setCurrentSlide(2)
        }, 500)
        return next
      }
      return next
    })
  }

  const prevSlide = () => {
    setIsTransitioning(true)
    setCurrentSlide(prev => {
      const next = prev - 1
      // If we're before the start (index 2), jump to end after transition
      if (next < 2) {
        setTimeout(() => {
          setIsTransitioning(false)
          setCurrentSlide(maxSlide)
        }, 500)
        return next
      }
      return next
    })
  }

  const openModal = () => {
    setGACustomEvent('trade_hall', {
      button_name: 'view_floor_plan_cta',
    })
    setIsModalOpen(true)
  }
  const closeModal = () => {
    setIsModalOpen(false)
  }

  const Controls = () => {
    const { zoomIn, zoomOut, resetTransform } = useControls()

    return (
      <ControlsContainer>
        <ControlButton
          onClick={() => zoomIn()}
          title="Zoom In"
          aria-label="Zoom in on floor plan"
          type="button"
        >
          +
        </ControlButton>
        <ControlButton
          onClick={() => zoomOut()}
          title="Zoom Out"
          aria-label="Zoom out on floor plan"
          type="button"
        >
          −
        </ControlButton>
        <ControlButton
          onClick={() => resetTransform()}
          title="Reset View"
          aria-label="Reset floor plan view"
          type="button"
        >
          ⌂
        </ControlButton>
      </ControlsContainer>
    )
  }

  React.useEffect(() => {
    setGACustomPageView({
      page: window.location.pathname,
      title: 'Trade_hall_page_main',
    })
  }, [])
  /*  */
  return (
    <>
      <Helmet>
        <title>SPACE at One Ayala</title>
        <meta
          name="description"
          content="SPACE at One Ayala: Makati CBD's premier event venue at EDSA & Ayala - Trade Hall and function rooms for unforgettable events in Metro Manila."
        />
        <meta property="og:title" content="SPACE at One Ayala" />
      </Helmet>
      <MainContainer>
        <HeroSection ref={heroSectionRef}>
          <BackgroundVideo
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            controls={false}
            onLoadedData={playVideoWhenReady}
            onCanPlay={playVideoWhenReady}
          >
            <source src={bgVideo} type="video/mp4" />
          </BackgroundVideo>
          <HeroContent>
            <HeroLogoArea>
              <SpaceLogo
                src={spaceLogo}
                alt="SPACE at One Ayala Logo"
                fetchpriority="high"
                loading="eager"
              />
            </HeroLogoArea>
            <ButtonContainer>
              <PrimaryButton
                onClick={() => {
                  setGACustomEvent('trade_hall', {
                    button_name: 'book_now_cta',
                  })
                  window.open(
                    'https://app.smartsheet.com/b/form/ce011849ad0e4f0196c66fa8182b72c5',
                    '_blank',
                    'noopener,noreferrer',
                  )
                }}
                aria-label="Book your event at SPACE at One Ayala"
                type="button"
              >
                Book Now
              </PrimaryButton>
              <SecondaryButton
                onClick={() => {
                  setGACustomEvent('trade_hall', {
                    button_name: 'about_space_cta',
                  })
                  window.location.href = '#about-space'
                }}
                aria-label="Learn more about SPACE at One Ayala"
                type="button"
              >
                About SPACE
              </SecondaryButton>
            </ButtonContainer>
          </HeroContent>
        </HeroSection>

        <AboutSection id="about-space" bgImage={descriptionBg}>
          <ContentSection>
            <TriangleLogo
              src={triangleLogo}
              alt="SPACE at One Ayala Trade Hall Logo"
              loading="lazy"
              width="100"
              height="100"
            />
            <MainTitle as="h1">Where Moments Happen</MainTitle>

            <Description as="p">
              <BoldText>SPACE at One Ayala,</BoldText> isn't just a venue--it's
              a blank canvas for creativity. Designed for versatility, it adapts
              seamlessly to your vision, from intimate gatherings to grand
              celebrations and corporate events.{' '}
            </Description>

            <ViewStatsContainer>
              <StatsContainer>
                <StatsInfo>
                  <span
                    style={{
                      fontSize: '18px',
                      fontFamily: 'var(--font-family-ayala-display-sans)',
                    }}
                  >
                    Total Exhibition Areas
                  </span>
                  <StatsMeasurements>
                    <StatsNumber>2,700</StatsNumber>sqm |
                    <StatsNumber> 29,000</StatsNumber>sqft
                  </StatsMeasurements>
                </StatsInfo>

                <ViewFloorPlanButton
                  type="button"
                  onClick={openModal}
                  aria-label="View floor plan of SPACE at One Ayala"
                >
                  View Floor Plan
                </ViewFloorPlanButton>
              </StatsContainer>

              <FeaturesContainer>
                {TRADE_HALL_FEATURES.map((feature, index) => (
                  <React.Fragment key={feature.number}>
                    <FeatureItem>
                      <FeatureNumber>{feature.number}</FeatureNumber>
                      <FeatureText>
                        {feature.lines[0]} <br />
                        {feature.lines[1]}
                      </FeatureText>
                    </FeatureItem>
                    {index < TRADE_HALL_FEATURES.length - 1 && (
                      <Divider $hideOnMobile={index === 1} />
                    )}
                  </React.Fragment>
                ))}
              </FeaturesContainer>
            </ViewStatsContainer>

            <TitleTaglineContainer>
              <TaglineLogo
                src={taglineLogo}
                alt="SPACE at One Ayala Tagline"
                fetchpriority="high"
                loading="eager"
              />
            </TitleTaglineContainer>

            <CarouselContainer>
              <CarouselTrack
                currentSlide={currentSlide}
                isTransitioning={isTransitioning}
                data-carousel-track
              >
                {infiniteImages.map((image, index) => (
                  <CarouselSlide key={index} isActive={index === currentSlide}>
                    <CarouselImage
                      src={image.src}
                      alt={image.alt}
                      loading={index <= currentSlide + 1 ? 'eager' : 'lazy'}
                      decoding="async"
                      width="600"
                      height="320"
                    />
                    {infiniteImages[index + 2] && (
                      <CarouselImage
                        src={infiniteImages[index + 2].src}
                        alt={infiniteImages[index + 2].alt}
                        hideOnMobile
                        loading={index <= currentSlide + 1 ? 'eager' : 'lazy'}
                        decoding="async"
                        width="600"
                        height="320"
                      />
                    )}
                  </CarouselSlide>
                ))}
              </CarouselTrack>
              <CarouselArrow
                direction="left"
                onClick={prevSlide}
                aria-label="Previous slide"
                type="button"
              >
                ‹
              </CarouselArrow>
              <CarouselArrow
                direction="right"
                onClick={nextSlide}
                aria-label="Next slide"
                type="button"
              >
                ›
              </CarouselArrow>
            </CarouselContainer>

            <SpaceDescriptionContainer>
              <DescriptionRow>
                <DescriptionCard>
                  <DescriptionCardTitle>
                    Versatile Event Spaces
                  </DescriptionCardTitle>
                  <DescriptionCardText>
                    Our 2,700 sqm venue features four pillarless halls and three
                    modern function rooms—perfect for conventions, exhibits,
                    corporate launches, banquets, and private celebrations. With
                    flexible layouts, we adapt to gatherings of every size, from
                    intimate boardrooms to grand-scale conferences.
                  </DescriptionCardText>
                </DescriptionCard>
                <DescriptionCard variant="purple">
                  <DescriptionCardTitle>
                    State-of-the-Art Facilities
                  </DescriptionCardTitle>
                  <DescriptionCardText>
                    At SPACE at One Ayala, every detail is designed for seamless
                    experiences. Enjoy exclusive access to an executive lounge,
                    fully equipped catering halls with kitchens, three service
                    elevators, and a car lift for efficient event logistics.
                  </DescriptionCardText>
                </DescriptionCard>
              </DescriptionRow>
              <DescriptionRow>
                <DescriptionCard variant="purple">
                  <DescriptionCardTitle>
                    Prime Location in Makati
                  </DescriptionCardTitle>
                  <DescriptionCardText>
                    Situated in Ayala Center, our venue offers unmatched
                    accessibility and brand visibility. Surrounded by hotels,
                    retail, and transport hubs, it is the ideal destination for
                    both local and international guests.
                  </DescriptionCardText>
                </DescriptionCard>
                <DescriptionCard>
                  <DescriptionCardTitle>
                    Your Event, Elevated
                  </DescriptionCardTitle>
                  <DescriptionCardText>
                    Whether you're planning a trade show, business summit,
                    cultural showcase, or social celebration, SPACE at One Ayala
                    provides the setting, support, and sophistication to make it
                    truly unforgettable.
                  </DescriptionCardText>
                </DescriptionCard>
              </DescriptionRow>
            </SpaceDescriptionContainer>

            <FinalTitle as="h2">
              <HighlightedText>SPACE at One Ayala</HighlightedText>
              <br />
              Makati's iconic trade hall and event destination.
            </FinalTitle>

            <SocialFollowSection aria-labelledby="trade-hall-social-heading">
              <SocialFollowHeading id="trade-hall-social-heading">
                Follow us on socials
              </SocialFollowHeading>
              <SocialIconsRow>
                <SocialIconLink
                  href={TRADE_HALL_SOCIAL_LINKS.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="SPACE at One Ayala on Facebook"
                >
                  <SocialIconImg
                    src={socialFacebook}
                    alt=""
                    width="56"
                    height="56"
                    decoding="async"
                  />
                </SocialIconLink>
                <SocialIconLink
                  href={TRADE_HALL_SOCIAL_LINKS.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="SPACE at One Ayala on Instagram"
                >
                  <SocialIconImg
                    src={socialInstagram}
                    alt=""
                    width="56"
                    height="56"
                    decoding="async"
                  />
                </SocialIconLink>
              </SocialIconsRow>
            </SocialFollowSection>
          </ContentSection>
        </AboutSection>
      </MainContainer>

      {/* Modal */}
      {isModalOpen && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Floor Plan</ModalTitle>
              <CloseButton
                onClick={closeModal}
                aria-label="Close floor plan modal"
                type="button"
              >
                &times;
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <TransformWrapper
                defaultScale={1}
                defaultPositionX={0}
                defaultPositionY={0}
              >
                <Controls />
                <TransformComponent
                  wrapperStyle={{ width: '100%', height: '100%' }}
                  contentStyle={{ width: '100%', height: '100%' }}
                >
                  <img
                    src={floorPlanImage}
                    alt="SPACE at One Ayala floor plan showing exhibition areas, function rooms, and facilities"
                    loading="lazy"
                    decoding="async"
                    width="1200"
                    height="800"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </TransformComponent>
              </TransformWrapper>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  )
}
